import MainSlideTimeline from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/player-state-animation/player-main-seq-timeline';
import ExecuteBaseManager from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/execute-manager/execute-base-manager';
import { reaction } from 'mobx';
import { PlayerRenderService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player';
import TaskManager from '@src/slide/infrastructure/module/dependent/taskManager';

import { MAIN_TIMELINE_OPTIONS } from '../anim-constants';
import { getMainAnimList } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';

import { IObserverParams, ISlideTaskKeyObj } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-interface';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import type { IPlayManager } from '@src/slide/application/service/play-service/play-manager.interface';


export default class CreateAnimTimelineManager extends ExecuteBaseManager {
    public static instance;
    public static getInstance(): CreateAnimTimelineManager {
        if (!this.instance) {
            this.instance = new CreateAnimTimelineManager();
        }
        return this.instance;
    }

    private mediaManager: IMediaManager;
    private playManager: IPlayManager;

    public init(presentation: IPresentationModel, mediaManager: IMediaManager, playManager: IPlayManager): void {
        this.presentation = presentation;
        this.mediaManager = mediaManager;
        this.playManager = playManager;
    }


    protected needStopExecuteCurrentSlide = (slideModel: ISlideModel): boolean => !this.checkHasAnim(slideModel);

    //  监听动画数据转换是否完成，以及渲染树children是否创建完成
    protected addExecuteObserver = ({ slideModel, slideComponent, callback }: IObserverParams): void => {
        const createdReaction = reaction(
            () => ({ animConverted: slideModel.animConverted, hasBuilt: slideComponent.slideElementsHasBuilt }),
            ({ animConverted, hasBuilt }) => {
                if (animConverted && hasBuilt) {
                    callback?.();
                    // 释放reaction监听
                    createdReaction?.();
                }
            },
        );
    };


    /**
     * override
     * 对于已添加任务队列，但并未执行的时间线创建，取消该任务
     * @param needExecuteSlideIndexList 当前需要进行提前处理的slideList
     * 之所以取消入队任务，是为了和演示态下渲染树创建策略保持一致，即只处理当前页及相邻页数据
     * 如果两者策略不一致，会在备注视图中出现以下问题：
     * 演示态下渲染树已经销毁，但是timeline的创建仍在任务队列中，导致切到该页时timeline中的slideComponent和该页正在渲染的slideComponent不一致，
     * 从而引发动画不播放的问题，以及无法找到application的问题
     */
    protected handleUnexecutedTask = (needExecuteSlideIndexList: ISlideTaskKeyObj[]): void => {
        this.unexecutedTaskKeySet.forEach((unexecutedTaskKey) => {
            needExecuteSlideIndexList.forEach(({ taskKey }) => {
                if (unexecutedTaskKey === taskKey) return;
                TaskManager.cancelTaskByKey(unexecutedTaskKey);
            });
        });
    };

    // 检查slide的动画时间线是否创建完成
    protected checkIsExecuted = (slideIndex: number): boolean => {
        const slideModel = this.presentation.children[slideIndex];
        return !!slideModel?.mainSlideTimeline;
    };
    // 创建动画时间线
    protected taskFunc = ({ slideModel }: IObserverParams): void | undefined => {
        const slideComponent = PlayerRenderService.getInstance().getRenderTree(slideModel)?.slideComponent;
        if (!slideComponent) return;
        if (slideComponent.slideElementsHasBuilt && slideModel.animConverted) {
            this.mountTimeline(slideModel, slideComponent);
        } else {
            this.addExecuteObserver({
                slideModel,
                slideComponent,
                callback: () => {
                    this.mountTimeline(slideModel, slideComponent);
                },
            });
        }
    };

    // 创建taskKey
    protected createTaskKey = (slideKey: string): string => `create-timeline-${slideKey}`;

    /**
     * 当前页model挂载动画时间线
     * @param slideModel 当前页model
     * @param slideComponent 当前页canvas渲染树
     * @returns
     */
    private mountTimeline = (slideModel, slideComponent): void | undefined => {
        const animList = getMainAnimList(slideModel);
        if (!animList) return;
        const tempSlideModel = slideModel;
        if (slideComponent.isDispose) {
            tempSlideModel.mainSlideTimeline = null;
        } else {
            tempSlideModel.mainSlideTimeline = new MainSlideTimeline({
                animList,
                slideComponent,
                mediaManager: this.mediaManager,
                options: {
                    ...MAIN_TIMELINE_OPTIONS,
                    autoPlay: slideModel.transition?.autoAdvance,
                },
                playManager: this.playManager,
            });
        }
    };

    // 判断当前页是否有动画数据
    private checkHasAnim(slideModel: ISlideModel): boolean {
        return !!slideModel.timing;
    }
}
